import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { addDays, addMonths, format, isSameDay, startOfMonth } from "date-fns";
import {
  convertTime12to24,
  getDateFromOffset,
  getLocale,
} from "../../../helpers/dates";
import WelletContext from "../../../context/WelletContext";
import { getReservationCancel } from "../../../services/welletService";
import { lockSchedule } from "../../../services/reservations/reservationsService";
import SuccessScreen from "../../ReservationSuccess/SuccessScreen";
import ErrorPage from "../../Error/ErrorPage";
import "../../../components/CandelOrder/cancelOrder.css";
import "./modifyReservation.css";

const IconCalendar = () => (
  <svg className="mr__chip-icon" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="1" />
    <path d="M3 9h18M8 3v4M16 3v4" />
  </svg>
);
const IconClock = () => (
  <svg className="mr__chip-icon" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 16 14" />
  </svg>
);
const IconUser = () => (
  <svg className="mr__chip-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const ModifyReservation = () => {
  const { t } = useTranslation();
  const welletContext = useContext(WelletContext);
  const language = useSelector((state) => state.app.language);
  const locale = getLocale(language);
  const navigate = useNavigate();
  const { token } = useParams();

  const [reservationInfo, setReservationInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(null);
  const [calMonth, setCalMonth] = useState(null);

  const [intervals, setIntervals] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);

  const [selectedPax, setSelectedPax] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedPerformance, setSelectedPerformance] = useState(null);
  const [selectedSeatType, setSelectedSeatType] = useState(null);

  const [openPicker, setOpenPicker] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [scheduleNotAvailable, setScheduleNotAvailable] = useState(false);
  const [modifying, setModifying] = useState(false);
  const [response, setResponse] = useState(null);
  const [externalPlatformDetail, setExternalPlatformDetail] = useState(null);

  const originalRef = useRef({
    paxs: null,
    date: null,
    time: null,
    attribute: null,
  });

  const isExternal = reservationInfo?.externalPlatform;

  useEffect(() => {
    (async () => {
      try {
        const result = await getReservationCancel(token);
        setReservationInfo(result);
        if (result?.reservationDate) {
          const d = new Date(result.reservationDate);
          const dateStr = format(d, "yyyy-MM-dd");
          const timeStr = result.reservationDate.split("T")[1]?.substring(0, 5);
          originalRef.current = {
            paxs: result.paxs,
            date: dateStr,
            time: timeStr,
            attribute: result.attribute ?? null,
          };
          setSelectedPax(result.paxs);
          setSelectedDate(d);
          setSelectedPerformance(timeStr);
          setSelectedSeatType(result.attribute ?? null);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const fetchShow = async () => {
    if (!reservationInfo?.showId) return;
    try {
      const result = await welletContext.apis.tickets.get("/shows/get", {
        params: { showId: reservationInfo.showId },
      });
      const todayDate = new Date(getDateFromOffset(result.data.utcOffset));
      setToday(todayDate);
      setCalMonth(startOfMonth(todayDate));
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSchedule = async (date, pax) => {
    if (!reservationInfo?.showId || !date || !pax) return;
    setLoadingSchedule(true);
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const result = await welletContext.apis.tickets.get(
        `/shows/GetSchedule?showId=${reservationInfo.showId}&date=${dateStr}&reservationType=RESTAURANT&paxs=${pax}`
      );
      const rawSchedules = result.data?.schedules || [];
      const formatted = rawSchedules
        .map((time) => {
          const time24 = new Date(dateStr + "T" + convertTime12to24(time.timeStart));
          return {
            time: time24,
            timeMinutesStart: time.timeMinutesStart,
            soldOut: time.isSoldOut,
            attributes: time.attributes || [],
          };
        })
        .sort((a, b) => a.timeMinutesStart - b.timeMinutesStart);
      setIntervals(formatted);
    } catch (err) {
      console.error(err);
      setIntervals([]);
    } finally {
      setLoadingSchedule(false);
    }
  };

  const fetchExternalPlatform = async () => {
    if (!reservationInfo?.showId) return;
    try {
      const result = await welletContext.apis.tickets.get(
        `/shows/${reservationInfo.showId}/external-plaftorm/config?reservationType=RESTAURANT`
      );
      setExternalPlatformDetail(result.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (reservationInfo?.showId) {
      fetchShow();
      if (isExternal) fetchExternalPlatform();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationInfo?.showId, isExternal]);

  useEffect(() => {
    if (selectedDate && selectedPax && reservationInfo?.showId) {
      fetchSchedule(selectedDate, selectedPax);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedPax, reservationInfo?.showId]);

  useEffect(() => {
    setScheduleNotAvailable(false);
    setErrorMessage(null);
  }, [selectedPerformance, selectedDate, selectedPax, selectedSeatType]);

  useEffect(() => {
    if (!openPicker) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [openPicker]);

  const seatingOptions = useMemo(() => {
    const present = new Set(intervals.flatMap((i) => i.attributes || []));
    const dictionary = t("translateAttribute", { returnObjects: true }) || {};
    return Object.entries(dictionary)
      .filter(([id]) => present.has(id))
      .map(([id, label]) => ({ id, label }));
  }, [intervals, t]);

  useEffect(() => {
    if (!seatingOptions.length) return;
    if (!seatingOptions.some((o) => o.id === selectedSeatType)) {
      const fallback =
        seatingOptions.find((o) => o.id === "default") || seatingOptions[0];
      setSelectedSeatType(fallback.id);
    }
  }, [seatingOptions, selectedSeatType]);

  const filteredSlots = useMemo(() => {
    if (!selectedSeatType) return intervals;
    return intervals.filter(
      (s) => !s.attributes.length || s.attributes.includes(selectedSeatType)
    );
  }, [intervals, selectedSeatType]);

  useEffect(() => {
    if (loadingSchedule) return;
    if (filteredSlots.length === 0) return;
    if (!selectedPerformance) return;
    const stillAvailable = filteredSlots.some(
      (s) => format(s.time, "HH:mm") === selectedPerformance && !s.soldOut
    );
    if (!stillAvailable) setSelectedPerformance(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeatType, filteredSlots, loadingSchedule]);

  const slotsScrollRef = useRef(null);

  useEffect(() => {
    const targetTime = selectedPerformance || originalRef.current.time;
    if (!targetTime || filteredSlots.length === 0) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const container = slotsScrollRef.current;
        if (!container) return;

        let el = container.querySelector(".slot-pill.selected");

        if (!el) {
          const [sh, sm] = targetTime.split(":").map(Number);
          const targetMinutes = sh * 60 + sm;
          let closestTime = null;
          let minDiff = Infinity;
          filteredSlots.forEach((s) => {
            const t24 = format(s.time, "HH:mm");
            const [h, m] = t24.split(":").map(Number);
            const diff = Math.abs(h * 60 + m - targetMinutes);
            if (diff < minDiff) {
              minDiff = diff;
              closestTime = t24;
            }
          });
          if (closestTime) {
            el = container.querySelector(`[data-time="${closestTime}"]`);
          }
        }

        if (!el) return;
        const cRect = container.getBoundingClientRect();
        const eRect = el.getBoundingClientRect();
        const desired =
          container.scrollTop +
          (eRect.top - cRect.top) -
          container.clientHeight / 2 +
          eRect.height / 2;
        const max = container.scrollHeight - container.clientHeight;
        container.scrollTo({
          top: Math.max(0, Math.min(desired, max)),
          behavior: "smooth",
        });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [selectedPerformance, filteredSlots]);

  const slotGroups = useMemo(() => {
    const lunch = [];
    const dinner = [];
    const late = [];
    filteredSlots.forEach((s) => {
      const h = s.time.getHours();
      if (h < 16) lunch.push(s);
      else if (h < 22) dinner.push(s);
      else late.push(s);
    });
    return { lunch, dinner, late };
  }, [filteredSlots]);

  const hasChanges = useMemo(() => {
    if (!reservationInfo || !selectedDate || !selectedPerformance) return false;
    const currentDate = format(selectedDate, "yyyy-MM-dd");
    return (
      selectedPax !== originalRef.current.paxs ||
      currentDate !== originalRef.current.date ||
      selectedPerformance !== originalRef.current.time ||
      selectedSeatType !== originalRef.current.attribute
    );
  }, [
    selectedPax,
    selectedDate,
    selectedPerformance,
    selectedSeatType,
    reservationInfo,
  ]);

  const changedFields = useMemo(() => {
    if (!reservationInfo || !selectedDate) return [];
    const currentDate = format(selectedDate, "yyyy-MM-dd");
    const fields = [];
    if (currentDate !== originalRef.current.date) fields.push(t("widget.date"));
    if (selectedPerformance !== originalRef.current.time)
      fields.push(t("widget.time"));
    if (selectedPax !== originalRef.current.paxs)
      fields.push(t("widget.guests"));
    if (selectedSeatType !== originalRef.current.attribute)
      fields.push(t("seatType"));
    return fields;
  }, [
    selectedPax,
    selectedDate,
    selectedPerformance,
    selectedSeatType,
    reservationInfo,
    t,
  ]);

  const handleModify = async () => {
    setModifying(true);
    setErrorMessage(null);
    try {
      let availabilityRefId = null;
      if (isExternal) {
        const lockResult = await lockSchedule(welletContext, {
          pax: selectedPax,
          reservationDateLocalTime:
            format(selectedDate, "yyyy-MM-dd") + "T" + selectedPerformance,
          reservationType: "RESTAURANT",
          showId: reservationInfo.showId,
          attribute: selectedSeatType,
        });
        if (lockResult?.success === false) {
          setScheduleNotAvailable(true);
          setModifying(false);
          return;
        }
        availabilityRefId = lockResult?.reservation_Token;
      }
      const datePerformance =
        format(selectedDate, "yyyy-MM-dd") + " " + selectedPerformance;
      const res = await welletContext.apis.tickets.put(
        "/reservation/ModifyReservation",
        {
          showId: reservationInfo.showId,
          paxs: selectedPax,
          token,
          CustomerName: reservationInfo.customerName ?? "",
          CustomerPhone: reservationInfo.customerPhone ?? "",
          Email: reservationInfo.customerEmail ?? "",
          Comments: reservationInfo.customerComments ?? "",
          Occasion: reservationInfo.customerOccasion ?? null,
          CountryCode: "MX",
          ReservationDateLocalTime: datePerformance,
          OldReservationId: reservationInfo.reservationId,
          attribute: selectedSeatType,
          ...(availabilityRefId && { availabilityRefId }),
        }
      );
      if (res?.status === 200) setResponse(true);
    } catch (error) {
      switch (error.response?.data?.errorType) {
        case "RESERVATION_NOT_FOUND":
          setErrorMessage(t("modifyReservationError.RESERVATION_NOT_FOUND"));
          break;
        case "RESERVATION_NOT_ALLOWED_FOR_MODIFY":
          setErrorMessage(
            t("modifyReservationError.RESERVATION_NOT_ALLOWED_FOR_MODIFY")
          );
          break;
        case "EXCEEDED_TIME_FOR_MODIFICATION":
          setErrorMessage(
            t("modifyReservationError.EXCEEDED_TIME_FOR_MODIFICATION")
          );
          break;
        case "HAS_MODIFICATION":
          setErrorMessage(t("modifyReservationError.HAS_MODIFICATION"));
          break;
        case "OLD_ORDER_ID_REQUIRED":
          setErrorMessage(t("modifyReservationError.OLD_ORDER_ID_REQUIRED"));
          break;
        default:
          setErrorMessage(t("modifyReservationError.DEFAULT"));
      }
    } finally {
      setModifying(false);
    }
  };

  if (response) {
    return (
      <SuccessScreen
        showName={reservationInfo?.showName}
        externalPlatform={reservationInfo?.externalPlatform}
      />
    );
  }

  if (loading) {
    return (
      <div className="mr">
        <div className="mr__loading">
          <div className="mr__spinner" />
        </div>
      </div>
    );
  }

  if (!reservationInfo) {
    return <ErrorPage />;
  }

  if (reservationInfo.status !== "CONFIRMED") {
    const venueName = reservationInfo?.showName || "";
    const extPlatform = reservationInfo?.externalPlatform;
    const showExtPlatform = extPlatform?.showPowerBy && extPlatform?.name;
    return (
      <div className="co">
        <header className="co__header">
          <span className="co__brand">{venueName}</span>
        </header>
        <main className="co__main">
          <div className="co__container">
            <div className="co__state">
              <div className="co__status-icon co__status-icon--warning">
                <svg viewBox="0 0 24 24">
                  <path d="M12 3L2.5 20.5h19L12 3z" />
                  <line x1="12" y1="10" x2="12" y2="14" />
                  <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
                </svg>
              </div>
              <div className="co__eyebrow co__eyebrow--success">
                <span className="co__eyebrow-rule" />
                <span className="co__eyebrow-diamond">◆</span>
                <span className="co__eyebrow-text">
                  {t("modifyReservationUnavailable.eyebrow")}
                </span>
                <span className="co__eyebrow-diamond">◆</span>
                <span className="co__eyebrow-rule" />
              </div>
              <h1 className="co__title">
                {t("modifyReservationUnavailable.title")}
              </h1>
              <p className="co__subtitle">
                {t("modifyReservationUnavailable.subtitle")}
              </p>
            </div>
          </div>
        </main>
        <footer className="co__footer">
          <p className="co__footer-powered">
            Powered by Wellet
            {showExtPlatform ? ` · ${extPlatform.name}` : ""}
          </p>
        </footer>
      </div>
    );
  }

  const venueName = reservationInfo.showName;
  const extPlatform = reservationInfo.externalPlatform;
  const showExtPlatform = extPlatform?.showPowerBy && extPlatform?.name;
  const dateChipValue = selectedDate
    ? format(selectedDate, "EEE d MMM", { locale })
    : "—";
  const timeChipValue = selectedPerformance
    ? format(
        new Date(`2000-01-01T${selectedPerformance}:00`),
        "h:mm a",
        { locale }
      )
    : "—";
  const guestsChipValue = selectedPax
    ? `${selectedPax} ${selectedPax > 1 ? t("people") : t("person")}`
    : "—";

  const originalDateText = originalRef.current.date
    ? format(new Date(originalRef.current.date + "T00:00:00"), "EEE d MMM", {
        locale,
      })
    : "";
  const originalTimeText = originalRef.current.time
    ? format(
        new Date(`2000-01-01T${originalRef.current.time}:00`),
        "h:mm a",
        { locale }
      )
    : "";
  const originalGuestsText = originalRef.current.paxs
    ? `${originalRef.current.paxs} ${
        originalRef.current.paxs > 1 ? t("people") : t("person")
      }`
    : "";
  const originalSeatText = originalRef.current.attribute
    ? t(`translateAttribute.${originalRef.current.attribute}`)
    : "";

  return (
    <div className="mr">
      <header className="mr__header">
        <span className="mr__brand">{venueName}</span>
      </header>

      <div className="mr__banner">
        <span className="mr__banner-text">
          {t("modifyReservationEdit.bannerPrefix")}{" "}
          <strong>{t("modifyReservationEdit.bannerHighlight")}</strong>{" "}
          {t("modifyReservationEdit.bannerMid", { venue: venueName })}{" "}
          {t("modifyReservationEdit.bannerCode")}{" "}
          <strong>{reservationInfo.referenceCode}</strong>
        </span>
      </div>

      <main className="mr__main">
        <div className="mr__container">
          <div className="mr__card">
            <div className="mr__card-head">
              <div className="mr__card-brand">{venueName}</div>
              <h1 className="mr__card-title">
                {t("modifyReservationEdit.title")}
              </h1>
            </div>

            <div className="mr__chips">
              <button
                type="button"
                className="mr__chip"
                onClick={() => setOpenPicker("date")}
              >
                <IconCalendar />
                <div className="mr__chip-content">
                  <span className="mr__chip-label">{t("widget.date")}</span>
                  <span className="mr__chip-value">{dateChipValue}</span>
                </div>
              </button>
              <button
                type="button"
                className="mr__chip"
                onClick={() => setOpenPicker("time")}
              >
                <IconClock />
                <div className="mr__chip-content">
                  <span className="mr__chip-label">{t("widget.time")}</span>
                  <span className="mr__chip-value">{timeChipValue}</span>
                </div>
              </button>
              <button
                type="button"
                className="mr__chip"
                onClick={() => setOpenPicker("guests")}
              >
                <IconUser />
                <div className="mr__chip-content">
                  <span className="mr__chip-label">{t("widget.guests")}</span>
                  <span className="mr__chip-value">{guestsChipValue}</span>
                </div>
              </button>
            </div>

            {seatingOptions.length > 1 && (
              <div className="mr__seating">
                {seatingOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`mr__seating-tab ${
                      selectedSeatType === opt.id ? "selected" : ""
                    }`}
                    onClick={() => setSelectedSeatType(opt.id)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            <div className="mr__slots-label">
              {t("modifyReservationEdit.availableTimes")}
            </div>

            {loadingSchedule ? (
              <div className="slots-grid-inline">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="slot-pill slot-pill--loading" />
                ))}
              </div>
            ) : filteredSlots.length === 0 ? (
              <div className="slots-empty">
                <div className="slots-empty__text">
                  {t("modifyReservationEdit.noSlots")}
                </div>
              </div>
            ) : (
              <div className="mr__slots-scroll" ref={slotsScrollRef}>
                <div className="slots-grid-inline">
                  {filteredSlots.map((slot) => {
                    const t24 = format(slot.time, "HH:mm");
                    const isSelected = selectedPerformance === t24;
                    const isUnavailable = slot.soldOut;
                    return (
                      <button
                        key={slot.time.toISOString()}
                        type="button"
                        data-time={t24}
                        className={`slot-pill ${
                          isSelected ? "selected" : ""
                        } ${isUnavailable ? "unavailable" : ""}`}
                        disabled={isUnavailable}
                        onClick={() => setSelectedPerformance(t24)}
                      >
                        {format(slot.time, "h:mm a")}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {hasChanges && (
              <div className="mr__changes">
                <div className="mr__changes-title">
                  {t("modifyReservationEdit.changesDetected")}
                </div>
                <div className="mr__changes-text">
                  {t("modifyReservationEdit.willModify", {
                    fields: changedFields.join(", "),
                  })}
                </div>
              </div>
            )}

            {errorMessage && <div className="mr__error">{errorMessage}</div>}
            {scheduleNotAvailable && (
              <div className="mr__error">{t("scheduleIsNotAvailable")}</div>
            )}

            <div className="mr__actions">
              <button
                type="button"
                className="mr__btn-primary"
                disabled={!hasChanges || modifying}
                onClick={handleModify}
              >
                {modifying
                  ? t("form.processing")
                  : t("modifyReservationEdit.saveChanges")}
              </button>
            </div>
          </div>

          {originalRef.current.date && (
            <div className="mr__info">
              {t("modifyReservationEdit.originalReservation")} ·{" "}
              <strong>
                {originalDateText} · {originalTimeText} · {originalGuestsText}
                {originalSeatText ? ` · ${originalSeatText}` : ""}
              </strong>
            </div>
          )}
        </div>
      </main>

      <footer className="mr__footer">
        <p className="mr__footer-powered">
          Powered by Wellet
          {showExtPlatform ? ` · ${extPlatform.name}` : ""}
        </p>
      </footer>

      <DatePickerOverlay
        open={openPicker === "date"}
        onClose={() => setOpenPicker(null)}
        today={today}
        calMonth={calMonth}
        setCalMonth={setCalMonth}
        selectedDate={selectedDate}
        onSelect={(d) => {
          setSelectedDate(d);
          setOpenPicker(null);
        }}
        locale={locale}
        t={t}
      />

      <GuestsPickerOverlay
        open={openPicker === "guests"}
        onClose={() => setOpenPicker(null)}
        value={selectedPax}
        onSelect={(n) => {
          setSelectedPax(n);
          setOpenPicker(null);
        }}
        t={t}
      />

      <TimePickerOverlay
        open={openPicker === "time"}
        onClose={() => setOpenPicker(null)}
        groups={slotGroups}
        loading={loadingSchedule}
        selectedTime={selectedPerformance}
        onSelect={(t24) => {
          setSelectedPerformance(t24);
          setOpenPicker(null);
        }}
        t={t}
      />
    </div>
  );
};

const DatePickerOverlay = ({
  open,
  onClose,
  today,
  calMonth,
  setCalMonth,
  selectedDate,
  onSelect,
  locale,
  t,
}) => {
  if (!calMonth || !today) return null;
  const monthStart = calMonth;
  const year = monthStart.getFullYear();
  const month = monthStart.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const offset = (firstDay + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const canGoPrev =
    monthStart.getFullYear() > today.getFullYear() ||
    (monthStart.getFullYear() === today.getFullYear() &&
      monthStart.getMonth() > today.getMonth());

  const todayStart = new Date(today);
  todayStart.setHours(0, 0, 0, 0);

  const upcoming = [];
  for (let i = 0; i < 14; i++) upcoming.push(addDays(today, i));

  return (
    <div
      className={`picker-overlay ${open ? "open" : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="picker-panel">
        <div className="picker-head">
          <button
            className="picker-back"
            type="button"
            onClick={onClose}
            aria-label="Back"
          >
            ←
          </button>
          <h4 className="picker-title">{t("widget.chooseDate")}</h4>
        </div>
        <div className="picker-body">
          <div className="picker-section">
            <div className="picker-section__label">
              {t("widget.upcomingDays")}
            </div>
          <div className="date-row">
            {upcoming.map((d) => {
              const isSel = selectedDate && isSameDay(d, selectedDate);
              const weekday = isSameDay(d, today)
                ? t("widget.todayShort")
                : isSameDay(d, addDays(today, 1))
                ? t("widget.tomorrowShort")
                : format(d, "EEE", { locale });
              return (
                <button
                  key={d.toISOString()}
                  type="button"
                  className={`date-pill ${isSel ? "selected" : ""}`}
                  onClick={() => onSelect(d)}
                >
                  <span className="date-pill__weekday">{weekday}</span>
                  <span className="date-pill__day">{format(d, "d")}</span>
                  <span className="date-pill__month">
                    {format(d, "MMM", { locale })}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="picker-section">
          <div className="picker-section__label">
            {t("widget.calendarTitle")}
          </div>
          <div className="calendar">
            <div className="calendar-head">
              <button
                type="button"
                className="calendar-nav"
                disabled={!canGoPrev}
                onClick={() => setCalMonth(addMonths(monthStart, -1))}
                aria-label="Previous month"
              >
                ‹
              </button>
              <div className="calendar-title">
                {format(monthStart, "MMMM yyyy", { locale })}
              </div>
              <button
                type="button"
                className="calendar-nav"
                onClick={() => setCalMonth(addMonths(monthStart, 1))}
                aria-label="Next month"
              >
                ›
              </button>
            </div>
            <div className="calendar-weekdays">
              {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => (
                <span key={i}>{d}</span>
              ))}
            </div>
            <div className="calendar-grid">
              {cells.map((cellDate, i) => {
                if (!cellDate) {
                  return <div key={i} className="cal-cell cal-cell--empty" />;
                }
                const past = cellDate < todayStart;
                const isToday = isSameDay(cellDate, today);
                const isSel = selectedDate && isSameDay(cellDate, selectedDate);
                let cls = "cal-cell";
                if (past) cls += " cal-cell--past";
                if (isToday && !isSel) cls += " cal-cell--today";
                if (isSel) cls += " cal-cell--selected";
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={past}
                    className={cls}
                    onClick={() => onSelect(cellDate)}
                  >
                    {format(cellDate, "d")}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

const TimePickerOverlay = ({
  open,
  onClose,
  groups,
  loading,
  selectedTime,
  onSelect,
  t,
}) => {
  const bodyRef = useRef(null);

  const sections = [
    { key: "lunch", label: t("widget.lunch"), slots: groups.lunch },
    { key: "dinner", label: t("widget.dinner"), slots: groups.dinner },
    { key: "late", label: t("widget.lateNight"), slots: groups.late },
  ];
  const hasAny =
    groups.lunch.length || groups.dinner.length || groups.late.length;

  useEffect(() => {
    if (!open || !selectedTime) return;
    const id = setTimeout(() => {
      const body = bodyRef.current;
      if (!body) return;

      let targetEl = body.querySelector(".slot-pill.selected");

      if (!targetEl) {
        const allSlots = [...groups.lunch, ...groups.dinner, ...groups.late];
        if (allSlots.length === 0) return;
        const [sh, sm] = selectedTime.split(":").map(Number);
        const selectedMinutes = sh * 60 + sm;
        let closestTime = null;
        let minDiff = Infinity;
        allSlots.forEach((s) => {
          const t24 = format(s.time, "HH:mm");
          const [h, m] = t24.split(":").map(Number);
          const diff = Math.abs(h * 60 + m - selectedMinutes);
          if (diff < minDiff) {
            minDiff = diff;
            closestTime = t24;
          }
        });
        if (closestTime) {
          targetEl = body.querySelector(`[data-time="${closestTime}"]`);
        }
      }

      if (!targetEl) return;
      let offset = 0;
      let node = targetEl;
      while (node && node !== body) {
        offset += node.offsetTop;
        node = node.offsetParent;
      }
      const target = offset - body.clientHeight / 2 + targetEl.offsetHeight / 2;
      const max = body.scrollHeight - body.clientHeight;
      body.scrollTo({
        top: Math.max(0, Math.min(target, max)),
        behavior: "smooth",
      });
    }, 60);
    return () => clearTimeout(id);
  }, [open, selectedTime, groups]);

  return (
    <div
      className={`picker-overlay ${open ? "open" : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="picker-panel">
        <div className="picker-head">
          <button
            className="picker-back"
            type="button"
            onClick={onClose}
            aria-label="Back"
          >
            ←
          </button>
          <h4 className="picker-title">{t("widget.chooseTime")}</h4>
        </div>
        <div className="picker-body" ref={bodyRef}>
          {loading ? (
            <div className="slots-grid-inline">
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="slot-pill slot-pill--loading" />
              ))}
            </div>
          ) : !hasAny ? (
            <div className="slots-empty">
              <div className="slots-empty__text">
                {t("widget.noSlotsTryAnother")}
              </div>
            </div>
          ) : (
            sections.map(
              ({ key, label, slots }) =>
                slots.length > 0 && (
                  <div key={key} className="all-times-section">
                    <div className="all-times-section__label">{label}</div>
                    <div className="slots-grid-inline">
                      {slots.map((slot) => {
                        const t24 = format(slot.time, "HH:mm");
                        const isSel = selectedTime === t24;
                        const isUnavailable = slot.soldOut;
                        return (
                          <button
                            key={slot.time.toISOString()}
                            type="button"
                            data-time={t24}
                            className={`slot-pill ${isSel ? "selected" : ""} ${
                              isUnavailable ? "unavailable" : ""
                            }`}
                            disabled={isUnavailable}
                            onClick={() => onSelect(t24)}
                          >
                            {format(slot.time, "h:mm a")}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )
            )
          )}
        </div>
      </div>
    </div>
  );
};

const GuestsPickerOverlay = ({ open, onClose, value, onSelect, t }) => {
  const [stepperValue, setStepperValue] = useState(Math.max(value || 9, 9));
  const [showStepper, setShowStepper] = useState((value || 0) > 8);

  const adjust = (delta) => {
    const next = Math.max(9, Math.min(15, stepperValue + delta));
    setStepperValue(next);
    onSelect(next);
  };

  return (
    <div
      className={`picker-overlay ${open ? "open" : ""}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="picker-panel">
        <div className="picker-head">
          <button
            className="picker-back"
            type="button"
            onClick={onClose}
            aria-label="Back"
          >
            ←
          </button>
          <h4 className="picker-title">{t("widget.howManyGuests")}</h4>
        </div>
        <div className="picker-body">
          <div className="guests-row">
          {[2, 3, 4, 5, 6, 7, 8].map((n) => (
            <button
              key={n}
              type="button"
              className={`guests-pill ${
                value === n && !showStepper ? "selected" : ""
              }`}
              onClick={() => {
                setShowStepper(false);
                onSelect(n);
              }}
            >
              {n}
            </button>
          ))}
          <button
            type="button"
            className={`guests-pill ${showStepper ? "selected" : ""}`}
            onClick={() => {
              setShowStepper(true);
              const v = Math.max(value || 9, 9);
              setStepperValue(v);
              onSelect(v);
            }}
          >
            9+
          </button>
        </div>
        {showStepper && (
          <div className="guests-stepper">
            <button
              type="button"
              className="guests-stepper__btn"
              disabled={stepperValue <= 9}
              onClick={() => adjust(-1)}
            >
              −
            </button>
            <span className="guests-stepper__value">{stepperValue}</span>
            <button
              type="button"
              className="guests-stepper__btn"
              disabled={stepperValue >= 15}
              onClick={() => adjust(1)}
            >
              +
            </button>
            <span className="guests-stepper__label">
              {t("widget.peopleLabel")}
            </span>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default ModifyReservation;
